import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { v4 as uuidv4 } from 'uuid';
import useSceneStore from './useSceneStore';
import useFurnitureStore from './useFurnitureStore';
import useTextStore from './useTextStore';
import useShapeStore from './useShapeStore';
import useLayerStore from './useLayerStore';
import useBackgroundStore from './useBackgroundStore';
import useLogoStore from './useLogoStore';
import useTableStore from './useTableStore';
import exportManager from '../ui/ExportManager';

const useFloorsStore = create(
  persist(
    (set, get) => ({
      floors: [],
      activeFloorId: null,
      isPanelOpen: false,

      initFloors: () => {
        const { floors } = get();
        if (floors.length === 0) {
          const id = uuidv4();
          const initialFloor = {
            id,
            name: 'Parter',
            thumbnail: null,
            state: get().captureCurrentState()
          };
          set({ floors: [initialFloor], activeFloorId: id });
        }
      },

      togglePanel: () => set((state) => ({ isPanelOpen: !state.isPanelOpen })),

      captureCurrentState: () => {
        // Zwracamy czyste obiekty danych, bez funkcji
        const getPlainData = (store) => {
          const state = store.getState();
          const data = {};
          Object.keys(state).forEach(key => {
            if (typeof state[key] !== 'function') {
              data[key] = JSON.parse(JSON.stringify(state[key]));
            }
          });
          return data;
        };

        return {
          scene: getPlainData(useSceneStore),
          furniture: getPlainData(useFurnitureStore),
          text: getPlainData(useTextStore),
          shape: getPlainData(useShapeStore),
          layer: getPlainData(useLayerStore),
          background: getPlainData(useBackgroundStore),
          logo: getPlainData(useLogoStore),
          table: getPlainData(useTableStore)
        };
      },

      applyState: (state) => {
        if (!state) return;
        
        // Funkcja do bezpiecznego nakładania stanu na store
        const applyToStore = (store, data) => {
          if (!data) return;
          store.setState(data);
        };

        // Nakładamy stany na wszystkie store'y
        // Używamy JSON.parse(JSON.stringify) aby zerwać wszelkie referencje
        const cleanState = JSON.parse(JSON.stringify(state));
        
        applyToStore(useSceneStore, cleanState.scene);
        applyToStore(useFurnitureStore, cleanState.furniture);
        applyToStore(useTextStore, cleanState.text);
        applyToStore(useShapeStore, cleanState.shape);
        applyToStore(useLayerStore, cleanState.layer);
        applyToStore(useBackgroundStore, cleanState.background);
        applyToStore(useLogoStore, cleanState.logo);
        applyToStore(useTableStore, cleanState.table);
      },

      resetWithProject: (projectData) => {
        const id = uuidv4();
        const initialFloor = {
          id,
          name: 'Parter',
          thumbnail: null,
          state: JSON.parse(JSON.stringify(projectData))
        };
        set({ 
          floors: [initialFloor], 
          activeFloorId: id,
          isPanelOpen: false 
        });
      },

      loadMultiFloorProject: (floors, activeId) => {
        const cleanFloors = JSON.parse(JSON.stringify(floors));
        const targetId = activeId || (cleanFloors.length > 0 ? cleanFloors[0].id : null);
        
        set({ 
          floors: cleanFloors, 
          activeFloorId: targetId,
          isPanelOpen: false
        });
        
        const activeFloor = cleanFloors.find(f => f.id === targetId);
        if (activeFloor) {
          get().applyState(activeFloor.state);
        }
      },

      switchFloor: (targetId) => {
        const { floors, activeFloorId, captureCurrentState, applyState } = get();
        if (targetId === activeFloorId) return;

        const updatedFloors = floors.map(floor => {
          if (floor.id === activeFloorId) {
            return {
              ...floor,
              thumbnail: exportManager.getThumbnailDataUrl(320),
              state: captureCurrentState()
            };
          }
          return floor;
        });

        const targetFloor = updatedFloors.find(f => f.id === targetId);
        if (targetFloor) {
          applyState(targetFloor.state);
          set({ floors: updatedFloors, activeFloorId: targetId });
        }
      },

      addFloor: (name = 'Nowe piętro') => {
        const { floors, captureCurrentState, applyState, activeFloorId } = get();
        const updatedFloors = floors.map(floor => {
            if (floor.id === activeFloorId) {
              return { ...floor, thumbnail: exportManager.getThumbnailDataUrl(320), state: captureCurrentState() };
            }
            return floor;
        });

        const id = uuidv4();
        const currentState = captureCurrentState();
        const newFloor = {
          id,
          name,
          thumbnail: null,
          state: {
            ...currentState,
            scene: { ...currentState.scene, corners: {}, walls: {}, elements: {}, rooms: {}, surfaces: {}, dimensionLines: {} },
            furniture: { ...currentState.furniture, furnitureItems: {} },
            text: { ...currentState.text, textItems: {} },
            shape: { ...currentState.shape, shapeItems: {} },
            background: { ...currentState.background, backgroundItem: null },
            logo: { ...currentState.logo, logoItems: {} },
            table: { ...currentState.table, tables: {} }
          }
        };

        applyState(newFloor.state);
        set({ floors: [...updatedFloors, newFloor], activeFloorId: id });
      },

      duplicateFloor: (floorId) => {
        const { floors, captureCurrentState, activeFloorId, applyState } = get();
        const sourceFloor = floors.find(f => f.id === floorId);
        if (!sourceFloor) return;

        let currentFloors = floors;
        if (floorId === activeFloorId) {
            currentFloors = floors.map(f => f.id === activeFloorId ? { 
                ...f, 
                thumbnail: exportManager.getThumbnailDataUrl(320),
                state: captureCurrentState() 
            } : f);
        }

        const newId = uuidv4();
        const newFloor = {
          ...JSON.parse(JSON.stringify(sourceFloor)), 
          id: newId,
          name: `${sourceFloor.name} (Kopia)`,
        };

        applyState(newFloor.state);
        set({ floors: [...currentFloors, newFloor], activeFloorId: newId });
      },

      removeFloor: (floorId) => {
        const { floors, activeFloorId, applyState } = get();
        if (floors.length <= 1) return;
        const filteredFloors = floors.filter(f => f.id !== floorId);
        if (floorId === activeFloorId) {
          const fallbackFloor = filteredFloors[0];
          applyState(fallbackFloor.state);
          set({ floors: filteredFloors, activeFloorId: fallbackFloor.id });
        } else {
          set({ floors: filteredFloors });
        }
      },

      renameFloor: (id, newName) => {
        set((state) => ({
          floors: state.floors.map(f => f.id === id ? { ...f, name: newName } : f)
        }));
      }
    }),
    {
      name: 'floorcraft-floors-storage',
      partialize: (state) => ({
        floors: state.floors,
        activeFloorId: state.activeFloorId
      })
    }
  )
);

export default useFloorsStore;